import { ServiceItemImage } from '@kalos/kalos-rpc';
import { Button, DataTablePagination, Skeleton, toast, useConfirm } from '@kalos/ui';
import { TrashIcon, UploadIcon } from '@radix-ui/react-icons';
import { useState } from 'react';

import { useS3FileObjectUrlQUery } from '../../../../hooks/react-query/useS3';
import {
  useServiceItemImageBatchGetQuery,
  useServiceItemImageCreateMutation,
  useServiceItemImageDeletionMutation,
} from '../../../../hooks/react-query/useServiceItemQuery';
import { compressImageFileForUpload } from '../../../../tools/fileUploadUtils';
import { RotateZoomImage } from '../../../AltGallery/main';
import { ServiceImageForm } from './ServiceImageForm';

interface ServiceItemsImagesGalleryProps {
  serviceItemId: number;
  fullScreenEnabled?: boolean;
}

export const ServiceItemsImagesGallery = ({
  serviceItemId,
  fullScreenEnabled = true,
}: ServiceItemsImagesGalleryProps) => {
  const [activeImageIdx, setActiveImageIdx] = useState(0);
  const [isAdding, setIsAdding] = useState(false);

  const serviceItemImageQuery = useServiceItemImageBatchGetQuery({
    filter: {
      serviceItemId,
    },
    enabled: true,
    select: (data) => {
      return {
        serviceItemImages: data.results,
      };
    },
  });

  const activeImage = serviceItemImageQuery.data?.serviceItemImages[activeImageIdx];
  const isImageQueryEnabled = !!activeImage;

  const imageQuery = useS3FileObjectUrlQUery({
    filter: {
      bucket: activeImage?.fileBucket ?? '',
      fileName: activeImage?.fileName ?? '',
      mimeType: activeImage?.mimeType ?? '',
    },
    enabled: isImageQueryEnabled,
  });

  const createServiceItemImageMutation = useServiceItemImageCreateMutation();
  const deleteServiceItemImageMutation = useServiceItemImageDeletionMutation();

  const onImagesAdd: React.ComponentProps<typeof ServiceImageForm>['onSubmit'] = async ({
    images,
  }) => {
    const results = await Promise.allSettled(
      images.map(async (image) =>
        createServiceItemImageMutation.mutateAsync({
          req: ServiceItemImage.create({ serviceItemId }),
          file: await compressImageFileForUpload(image),
        }),
      ),
    );
    const rejected = results.filter((r): r is PromiseRejectedResult => r.status === 'rejected');
    if (rejected.length > 0) {
      rejected.forEach((r) => console.error('Failed to upload service item photo:', r.reason));
      toast({
        title: 'Photo upload failed',
        description: `${rejected.length} of ${images.length} photo(s) did not upload. Please add them again.`,
        variant: 'destructive',
      });
      return;
    }
    setIsAdding(false);
  };

  const confirm = useConfirm();

  const onImageDelete = async () => {
    const serviceItemImage = serviceItemImageQuery.data?.serviceItemImages[activeImageIdx];
    if (!serviceItemImage) return;
    if (!(await confirm({ title: 'Are you sure you want to delete this image?' }))) return;

    deleteServiceItemImageMutation.mutateAsync(serviceItemImage);
    setActiveImageIdx((prev) => Math.max(prev - 1, 0));
  };

  const preview = serviceItemImageQuery.isFetching ? (
    <div className="mx-auto mt-4 w-full">
      <div className="flex flex-col gap-4">
        <Skeleton className="h-10 w-full" />
        <div className="h-[calc(100vh-320px)] min-h-[400px] w-full">
          <Skeleton className="mx-auto h-full w-full max-w-[700px]" />
        </div>
      </div>
    </div>
  ) : serviceItemImageQuery.data && serviceItemImageQuery.data.serviceItemImages.length == 0 ? (
    <span>No Images</span>
  ) : (
    <div className="mx-auto mt-4 w-full">
      <div className="flex flex-col gap-4">
        <DataTablePagination
          className="flex-1"
          currentPage={activeImageIdx}
          pageCount={serviceItemImageQuery.data?.serviceItemImages.length || 0}
          setPage={setActiveImageIdx}
          disabled={
            deleteServiceItemImageMutation.isPending || createServiceItemImageMutation.isPending
          }
        />
        <div className="h-[calc(100vh-320px)] min-h-[400px]">
          {imageQuery.isSuccess ? (
            <RotateZoomImage
              url={imageQuery.data}
              fullScreenEnabled={fullScreenEnabled}
              downloadFilename={activeImage?.fileName || 'service-item-image.png'}
            />
          ) : (
            <Skeleton className="mx-auto h-full w-full max-w-[700px]" />
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex w-full max-w-[800px] flex-col gap-2">
      {isAdding ? (
        <div className="flex justify-center gap-2">
          <ServiceImageForm
            onSubmit={onImagesAdd}
            disabled={createServiceItemImageMutation.isPending}
            isLoading={createServiceItemImageMutation.isPending}
            actions={
              <Button variant="secondary" onClick={() => setIsAdding(false)}>
                Cancel{' '}
              </Button>
            }
          />
        </div>
      ) : (
        <div className="flex gap-2">
          <Button onClick={() => setIsAdding(true)}>
            <UploadIcon />
            <span>Add Image</span>
          </Button>
          <Button
            isLoading={deleteServiceItemImageMutation.isPending}
            disabled={
              deleteServiceItemImageMutation.isPending ||
              serviceItemImageQuery.data?.serviceItemImages.length === 0
            }
            onClick={onImageDelete}
          >
            <TrashIcon />
            <span>Delete</span>
          </Button>
        </div>
      )}
      {!isAdding && preview}
    </div>
  );
};
